import React from "react";
import { AlertTriangle, Ghost, Landmark, DatabaseZap, UserX } from "lucide-react";
import PageHero from "../components/PageHero";
import SectionHead from "../components/SectionHead";
import Reveal from "../components/Reveal";
import FigureBlock from "../components/FigureBlock";

const CASES = [
    {
        who: "Robert Williams — Michigan, USA (2020)",
        what: "Arrested in front of his wife and young daughters and held overnight after a facial recognition system matched his driver's licence photo to grainy shoplifting footage. He was innocent; the system had matched the wrong man (Hill, 2020).",
    },
    {
        who: "Porcha Woodruff — Michigan, USA (2023)",
        what: "Eight months pregnant when she was arrested for carjacking and held while awaiting an AI match that investigators eventually dropped. She had been flagged by a system searching a decade-old licence photo (Hill, 2023).",
    },
    {
        who: "The pattern in the numbers",
        what: "US studies of real wrongful arrests report a common feature: nearly all known false-positive arrests have involved Black suspects. NIST testing explains why — many algorithms misidentify some demographic groups at rates up to 100 times higher than others (Grother et al., 2019; Hill, 2023).",
    },
];

export default function Risks() {
    return (
        <div>
            <PageHero
                fileNo="FR-NZ-05"
                lines={["Risks &", <>challenges — <i className="text-amber">the false positive</i></>]}
                lede="Our project centres on one failure mode above all: the technical false positive, where software misidentifies an innocent civilian as a violent offender. When automated accusation lands on people who have done nothing wrong, it does not stay a technical error — it becomes a psychological event, a legal injury, and a wound to public trust that does not close."
                meta={["SUBJECT: CORE CHALLENGE — LEAD: NICO"]}
            />

            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
                    <SectionHead
                        no="5.1"
                        kicker="Documented harm"
                        title="When the algorithm accuses the innocent"
                        lede="These are not hypotheticals. Each case below was a real wrongful arrest triggered by a facial recognition match — the exact scenario our research asks New Zealand to govern before it happens here."
                    />
                    <div className="grid lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {CASES.map((c, i) => (
                            <Reveal key={i} delay={i * 0.08} className="bg-ink">
                                <div className="p-7 sm:p-8 h-full" data-testid={`risk-case-card-${i + 1}`}>
                                    <AlertTriangle size={20} className="text-alert mb-6" />
                                    <h3 className="font-mono text-sm text-paper mb-3 leading-relaxed">{c.who}</h3>
                                    <p className="text-sm leading-relaxed text-ash">{c.what}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-sheet border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                    <div>
                        <SectionHead
                            no="5.2"
                            kicker="The chilling effect"
                            title="What watching does to the watched"
                        />
                        <div className="space-y-6 text-base leading-relaxed text-ash">
                            <Reveal>
                                <p>
                                    The chilling effect is the documented tendency of people who believe they are
                                    being watched to change lawful behaviour: avoiding certain streets, gatherings,
                                    expressions of political opinion. Surveillance scholars describe this as the
                                    asymmetry of power that constant watching produces — the watcher gains confidence,
                                    the watched lose freedom (Lyon, 2018).
                                </p>
                            </Reveal>
                            <Reveal delay={0.08}>
                                <p>
                                    Facial recognition sharpens this beyond anything CCTV alone can do. A camera that
                                    merely records may deter an act; a camera that <span className="text-paper">names</span> you
                                    from a distance, without your knowledge or consent, deters <span className="text-paper">presence itself</span>.
                                    The disproportionate error rates documented above mean the chill does not fall
                                    evenly: the groups most likely to be misidentified are the groups most likely to
                                    be watched most closely (Grother et al., 2019).
                                </p>
                            </Reveal>
                            <Reveal delay={0.14}>
                                <p>
                                    And the erosion compounds. Each wrongful accusation — each innocent person
                                    handcuffed because a system scored them a 90% match — is reported, remembered, and
                                    weighed by the public against every success the technology claims. Trust in
                                    policing is systemic: it holds the legitimacy that makes cooperation, testimony
                                    and consent to policing possible. False positives spend that legitimacy at four
                                    for the price of one.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                    <div className="space-y-10 lg:pt-4">
                        <FigureBlock
                            num="6"
                            src="https://images.unsplash.com/photo-1581429725675-53805dbce808?q=80&w=1600&auto=format&fit=crop"
                            alt="Overhead view of a crowd of people casting long shadows"
                            caption="From above, a crowd becomes a search space. The people casting these shadows have consented to nothing — and, under current New Zealand law, need never know they were queried."
                            ratio="4/3"
                        />
                        <Reveal delay={0.1}>
                            <div className="border border-alert/30 bg-alert/[0.05] p-6 sm:p-8">
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-alert mb-4">
                                    The stakes, quantified
                                </p>
                                <p className="text-sm sm:text-base leading-relaxed text-paper/85">
                                    Deploy FRT at a city-scale event scanning 50,000 faces with a system whose
                                    false-positive rate for a demographic group is 1 in 1,000 — and roughly 50
                                    innocent people are flagged as violent offenders before a single human officer
                                    reviews a single frame. At the 100× disparity NIST measured for some groups, the
                                    burden lands almost entirely on particular communities.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
                    <SectionHead no="5.3" kicker="The structural risk" title="Four challenges that outlast any one error" />
                    <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                        {[
                            {
                                icon: UserX,
                                t: "Wrongful accusation",
                                b: "The direct injury: detention, questioning, public humiliation — often before any human examines the evidence. An arrest is not a neutral event even when it ends in hours (Hill, 2020).",
                            },
                            {
                                icon: DatabaseZap,
                                t: "Insecure biometric data",
                                b: "A leaked password can be changed; a leaked face template cannot. Centralising searchable biometric signatures creates a breach risk whose harm is effectively permanent.",
                            },
                            {
                                icon: Landmark,
                                t: "The legislative lag",
                                b: "New Zealand's own record — a 2018 proof-of-concept, a 2020 unapproved trial — shows deployment outrunning governance. Police policy (2024) is self-imposed; a future administration could reverse it without any legislature noticing (Radio New Zealand, 2024).",
                            },
                            {
                                icon: Ghost,
                                t: "Legitimacy decay",
                                b: "Policing by consent assumes police can be believed when they name a suspect. Every AI false positive makes the next eyewitness doubt the next identification — and the next jury doubt the next match.",
                            },
                        ].map((r, i) => (
                            <Reveal key={r.t} delay={i * 0.06} className="bg-ink">
                                <div className="p-7 sm:p-8 h-full flex gap-5" data-testid={`risk-challenge-card-${i + 1}`}>
                                    <r.icon size={20} className="text-amber shrink-0 mt-1" />
                                    <div>
                                        <h3 className="font-display text-xl text-paper mb-3">{r.t}</h3>
                                        <p className="text-sm leading-relaxed text-ash">{r.b}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
